import { useQuery } from '@tanstack/react-query'
import { useMemo, useRef, useState, type ReactNode } from 'react'
import { ApiError } from '../api/client'
import { assessmentQuery } from '../api/dnscAssessment'
import { nis2DnscQuery, type DnscEvidence } from '../api/overview'
import { LEVEL_NAMES, NO_ASSESSMENT_RIGHT, proposalsById } from './assessment'
import { evidenceLine, type EvidenceState } from './dnsc'
import { hrefOf } from './href'
import { ScorePill } from './Nis2Assessment'
import {
  byFix,
  filterSummary,
  NO_SUMMARY_FILTERS,
  summarizeNis2,
  type FixControl,
  type GroupFilter,
  type SummaryFilters,
  type SummaryGroup,
  type SummaryRow,
} from './summary'

/**
 * Fila "Summary" din /overview/nis2: cerintele DNSC ale nivelului firmei in
 * trei grupe - ce ai, ce reparam noi, ce completati voi. Grupele: `summary.ts`.
 */

const ICONS: Record<EvidenceState, string> = {
  ok: 'fas fa-check-circle',
  warn: 'fas fa-exclamation-triangle',
  bad: 'fas fa-times-circle',
  unknown: 'fas fa-question-circle',
  info: 'fas fa-info-circle',
}

const GROUPS: Array<[GroupFilter, string]> = [
  ['all', 'All'],
  ['have', 'Have'],
  ['fix', 'We fix'],
  ['fill', 'You fill in'],
]

/* Numai cand serverul nu spune de ce (`cannotAnswer` gol). */
const PICK_COMPANY = 'choose one company in the company menu (top right): a self-assessment belongs to a single organisation'

function Gate({ state, title, children }: { state: string; title: string; children: ReactNode }) {
  return (
    <div className="adm-card nis2-sa nis2-sum" data-sum={state}>
      <h4>{title}</h4>
      <p className="nis2-muted">{children}</p>
    </div>
  )
}

function Head({ row }: { row: SummaryRow }) {
  const r = row.requirement
  return (
    <>
      <span className="nis2-dnsc-id">{r.code}</span>
      <span className={`nis2-dnsc-level nis2-dnsc-level-${r.level}`}>{LEVEL_NAMES[r.level]}</span>
      {row.key ? (
        <span className="nis2-dnsc-key" title="A key measure at your level: its own target, and it cannot be excluded">
          <i className="fas fa-key" aria-hidden="true"></i> key measure
        </span>
      ) : null}
    </>
  )
}

function Text({ row }: { row: SummaryRow }) {
  return (
    <p className="nis2-sum-text" lang="ro" title={row.requirement.text}>
      {row.requirement.text}
    </p>
  )
}

function Impl({ row }: { row: SummaryRow }) {
  return (
    <>
      <ScorePill label="Implementation" value={row.impl} />
      {row.implFrom === 'proposal' ? <span className="nis2-sum-from">proposed from data</span> : null}
    </>
  )
}

function FixLine({ fix, url, title }: { fix: string; url: string; title: string }) {
  const href = url ? hrefOf(url) : null
  if (!fix && !href) return null
  return (
    <div className="nis2-sum-do">
      {fix ? (
        <span>
          <i className="fas fa-wrench" aria-hidden="true"></i> {fix}
        </span>
      ) : null}
      {href ? (
        <a className="nis2-sum-open" href={href} aria-label={`Open ${title}`}>
          Open <i className="fas fa-arrow-circle-right" aria-hidden="true"></i>
        </a>
      ) : null}
    </div>
  )
}

/* Ce e de facut si "Open" numai pe un control picat; altfel doar cifra masurata. */
function EvidenceList({ items }: { items: DnscEvidence[] }) {
  return (
    <ul className="nis2-dnsc-evidence">
      {items.map((e) => {
        const line = evidenceLine(e)
        return (
          <li key={e.key} className={`nis2-dnsc-ev nis2-dnsc-ev-${line.state}`} data-sum-ev={e.key}>
            <i className={ICONS[line.state]} aria-hidden="true"></i>
            <div className="nis2-dnsc-ev-body">
              <span className="nis2-dnsc-ev-title" title={e.question || undefined}>
                {e.title}
              </span>
              <span className="nis2-dnsc-ev-text">{e.figure}</span>
              {e.sample.length > 0 ? <div className="nis2-dnsc-ev-where">where: {e.sample.join(', ')}</div> : null}
              {e.verdict === 'fail' ? <FixLine fix={e.fix ?? ''} url={e.url ?? ''} title={e.title} /> : null}
            </div>
          </li>
        )
      })}
    </ul>
  )
}

function FixRow({ row, target }: { row: SummaryRow; target: number }) {
  return (
    <div className="nis2-sum-row nis2-dnsc-tone-red" data-sum-row={row.requirement.code}>
      <div className="nis2-dnsc-req-head">
        <Head row={row} />
        <span className="nis2-sum-score">
          implementation {row.impl} / target {target}
        </span>
        {row.docMissing ? (
          <span className="nis2-sum-too" data-sum-doc-too>
            documentation missing too
          </span>
        ) : null}
      </div>
      <Text row={row} />
      <EvidenceList items={row.failing} />
    </div>
  )
}

function FillRow({ row, canAnswer, onAnswer }: { row: SummaryRow; canAnswer: boolean; onAnswer: (code: string) => void }) {
  const code = row.requirement.code
  return (
    <div className={`nis2-sum-row nis2-dnsc-tone-${row.doc === null && row.impl === null ? 'grey' : 'orange'}`} data-sum-row={code}>
      <div className="nis2-dnsc-req-head">
        <Head row={row} />
      </div>
      <Text row={row} />
      <div className="nis2-sa-req-line">
        <span className="nis2-sum-missing">
          Missing: <strong data-sum-missing-what>{row.missing}</strong>
        </span>
        <ScorePill label="Documentation" value={row.doc} />
        <Impl row={row} />
        {canAnswer ? (
          <span className="nis2-sa-req-buttons">
            <button type="button" className="btn btn-default btn-xs" data-sum-answer={code} aria-label={`Answer ${code}`} onClick={() => onAnswer(code)}>
              Answer
            </button>
          </span>
        ) : null}
      </div>
      {row.measured.length > 0 ? <EvidenceList items={row.measured} /> : null}
      {row.still ? (
        <div className="nis2-dnsc-still">
          <i className="fas fa-question-circle" aria-hidden="true"></i>
          <div>
            <strong>Not visible to the platform:</strong> {row.still}
          </div>
        </div>
      ) : null}
    </div>
  )
}

/* "By fix": un control picat o data; "appears in", nu "closes" - documentatia sau alt control pot lipsi. */
function FixControlRow({ c }: { c: FixControl }) {
  const n = c.requirements.length
  return (
    <div className="nis2-sum-row nis2-dnsc-tone-red" data-sum-fixctl={c.key}>
      <div className="nis2-dnsc-req-head">
        <span className="nis2-sum-fixtitle" title={c.question || undefined}>
          {c.title}
        </span>
        <span className="nis2-sum-figure">{c.figure}</span>
      </div>
      {c.sample.length > 0 ? <div className="nis2-dnsc-ev-where">where: {c.sample.join(', ')}</div> : null}
      <FixLine fix={c.fix} url={c.url} title={c.title} />
      <div className="nis2-sum-fixreqs">
        <span className="nis2-sum-appears">
          appears in {n} {n === 1 ? 'requirement' : 'requirements'} ({c.keyCount} key)
        </span>
        {c.requirements.map((r) => (
          <span
            key={r.code}
            className={`nis2-sum-chip${r.shown ? '' : ' nis2-sum-chip-hidden'}`}
            data-sum-fixreq={r.code}
            data-sum-hidden={r.shown ? undefined : ''}
            title={r.shown ? undefined : 'hidden by the current filters'}
          >
            {r.key ? <i className="fas fa-key" role="img" aria-label="key measure"></i> : null}
            {r.code}
          </span>
        ))}
      </div>
    </div>
  )
}

function HaveRow({ row }: { row: SummaryRow }) {
  return (
    <div className="nis2-sum-have-row" data-sum-row={row.requirement.code}>
      <Head row={row} />
      <span className="nis2-sum-have-text" lang="ro" title={row.requirement.text}>
        {row.requirement.text}
      </span>
      <span className="nis2-sum-have-scores">
        <ScorePill label="Documentation" value={row.doc} na={row.na} />
        {row.na ? <ScorePill label="Implementation" value={null} na /> : <Impl row={row} />}
      </span>
    </div>
  )
}

export function Nis2Summary({ onOpenDnsc }: { onOpenDnsc: (code: string | null) => void }) {
  const q = useQuery(assessmentQuery)
  /* Aceleasi doua cereri ca fila DNSC: din cache, daca a fost deschisa. */
  const proposalsQ = useQuery({ ...nis2DnscQuery, enabled: q.data?.ready === true })
  const proposals = useMemo(() => proposalsById(proposalsQ.data), [proposalsQ.data])
  const page = q.data
  const summary = useMemo(() => (page?.ready ? summarizeNis2(page, proposals) : null), [page, proposals])
  const [filters, setFilters] = useState<SummaryFilters>(NO_SUMMARY_FILTERS)
  const [fixView, setFixView] = useState<'requirement' | 'fix'>('requirement')
  const view = useMemo(() => (summary ? filterSummary(summary, filters) : null), [summary, filters])
  /* Numaratoarea pe toate cerintele "we fix"; filtrele aleg doar controalele aratate. */
  const fixes = useMemo(() => (summary && view ? byFix(summary.fix, view.fix, filters.search) : []), [summary, view, filters.search])
  const searchBox = useRef<HTMLInputElement>(null)
  const title = 'Summary - what you have, what we fix, what you fill in'

  if (q.isPending) {
    return (
      <Gate state="loading" title={title}>
        Reading the DNSC self-assessment...
      </Gate>
    )
  }
  if (q.isError) {
    const forbidden = q.error instanceof ApiError && q.error.status === 403
    return (
      <Gate state={forbidden ? 'forbidden' : 'error'} title={title}>
        <i className="fas fa-lock"></i> {forbidden ? NO_ASSESSMENT_RIGHT : 'The self-assessment could not be read.'}
      </Gate>
    )
  }
  if (!page || !page.ready) {
    return (
      <Gate state="off" title={title}>
        <i className="fas fa-database"></i> {page?.reason ?? 'not available on this installation'}
      </Gate>
    )
  }
  if (!page.company) {
    const why = page.cannotAnswer || PICK_COMPANY
    return (
      <Gate state="company" title={title}>
        <i className="fas fa-building"></i> Pick a company: {why}.
      </Gate>
    )
  }
  if (!summary && page.level) {
    return (
      <Gate state="off" title={title}>
        <i className="fas fa-database"></i> the rules of the {LEVEL_NAMES[page.level.level]} level are not loaded on this installation
      </Gate>
    )
  }
  if (!summary) {
    return (
      <Gate state="no-level" title={title}>
        The assurance level is not set yet - it decides which requirements are on your form and the targets.{' '}
        {page.canAnswer ? (
          <button type="button" className="btn btn-link btn-xs" data-sum-set-level onClick={() => onOpenDnsc(null)}>
            Set it in the DNSC self-assessment
          </button>
        ) : null}
      </Gate>
    )
  }
  if (proposalsQ.isPending || !view) {
    return (
      <Gate state="loading" title={title}>
        Reading what the platform measures...
      </Gate>
    )
  }

  const { counts, keyCounts, target } = summary
  const canAnswer = page.canAnswer === true
  /* `ready: false` = masuratoarea a picat pe server; ca la o eroare, nimic nu e "masurat". */
  const noProposals = proposalsQ.isError || proposalsQ.data?.ready === false
  /* Contoarele de sus raman totalurile; filtrele aleg numai ce se vede dedesubt. */
  const narrowed = filters.keyOnly || filters.search.trim() !== ''
  const filtered = narrowed || filters.group !== 'all'
  const visible = (g: SummaryGroup) => (filters.group === 'all' || filters.group === g) && (!narrowed || view[g].length > 0)
  /* Fara filtre, grupele se arata si goale, ca pana acum; cu filtre si nimic gasit, numai mesajul. */
  const lists = !filtered || view.shown > 0
  const clear = () => {
    setFilters(NO_SUMMARY_FILTERS)
    searchBox.current?.focus()
  }
  const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`
  const active = [
    filters.search.trim() !== '' ? `the search "${filters.search.trim()}"` : '',
    filters.keyOnly ? '"Key measures only"' : '',
    filters.group !== 'all' ? `the group "${GROUPS.find(([g]) => g === filters.group)?.[1]}"` : '',
  ].filter((a) => a !== '')

  return (
    <div className="adm-card nis2-sa nis2-sum" data-sum="ready">
      <div className="nis2-dnsc-head">
        <h4>{title}</h4>
        <p className="nis2-letter-asks">
          Every requirement on your {LEVEL_NAMES[summary.level]} form, in one place only. You have it when both scores - is it written
          down, is it done in practice - reach {target}, the key-measure target of your level. The implementation is your answer, or
          else what the platform measures; the documentation is always your answer.
        </p>
      </div>

      {!canAnswer && page.cannotAnswer ? (
        <p className="nis2-sa-readonly" data-sum-readonly>
          <i className="fas fa-eye"></i> Read only: {page.cannotAnswer}.
        </p>
      ) : null}

      <div className="nis2-dnsc-summary" data-sum-stats>
        <div className={`nis2-dnsc-stat nis2-dnsc-stat-${counts.missing === 0 ? 'green' : 'grey'}`}>
          <span className="nis2-dnsc-stat-value" data-sum-have>
            {counts.have} <small>of {counts.total}</small>
          </span>
          <span className="nis2-dnsc-stat-label">Have - both scores at {target} or more, or N/A</span>
        </div>
        <div className={`nis2-dnsc-stat nis2-dnsc-stat-${counts.missing > 0 ? 'red' : 'green'}`}>
          <span className="nis2-dnsc-stat-value" data-sum-missing>
            {counts.missing}
          </span>
          <span className="nis2-dnsc-stat-label">
            Missing - <span data-sum-split>we fix {counts.fix} + you fill in {counts.fill}</span>
          </span>
        </div>
        <div className={`nis2-dnsc-stat nis2-dnsc-stat-${keyCounts.missing === 0 ? 'green' : 'grey'}`}>
          <span className="nis2-dnsc-stat-value" data-sum-key-have>
            {keyCounts.have} <small>of {keyCounts.total}</small>
          </span>
          <span className="nis2-dnsc-stat-label">Key measures you have</span>
        </div>
        <div className={`nis2-dnsc-stat nis2-dnsc-stat-${keyCounts.missing > 0 ? 'red' : 'green'}`}>
          <span className="nis2-dnsc-stat-value" data-sum-key-missing>
            {keyCounts.missing}
          </span>
          <span className="nis2-dnsc-stat-label">
            Key measures missing - <span data-sum-key-split>we fix {keyCounts.fix} + you fill in {keyCounts.fill}</span>
          </span>
        </div>
      </div>

      <div className="nis2-dnsc-filters nis2-sum-filters" data-sum-filters>
        <div className="btn-group btn-group-sm" role="group" aria-label="Group">
          {GROUPS.map(([g, label]) => (
            <button
              key={g}
              type="button"
              className={`btn btn-default${filters.group === g ? ' active' : ''}`}
              aria-pressed={filters.group === g}
              data-sum-filter={g}
              onClick={() => setFilters((f) => ({ ...f, group: g }))}
            >
              {label} <span className="nis2-sum-n">{view.groupCounts[g]}</span>
            </button>
          ))}
        </div>
        <label className="nis2-sum-keyonly">
          <input type="checkbox" checked={filters.keyOnly} data-sum-key-only onChange={(e) => setFilters((f) => ({ ...f, keyOnly: e.target.checked }))} />{' '}
          <i className="fas fa-key" aria-hidden="true"></i> Key measures only
        </label>
        <input
          ref={searchBox}
          type="search"
          className="form-control input-sm nis2-sum-search"
          placeholder="Code, text, machine, account, control..."
          aria-label="Search the requirements"
          value={filters.search}
          data-sum-search
          onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
        />
        <span className="nis2-dnsc-count" data-sum-count>
          <span aria-live="polite">
            {view.shown} of {view.total} shown
          </span>
          {filtered ? (
            <button type="button" className="btn btn-link btn-xs" data-sum-clear onClick={clear}>
              clear filters
            </button>
          ) : null}
        </span>
      </div>

      {noProposals ? (
        <p className="nis2-muted nis2-sa-noprop" data-sum-noprop>
          What the platform measures could not be read, so nothing is on &quot;we fix&quot;: the groups come from your answers only.
        </p>
      ) : null}

      {filtered && view.shown === 0 ? (
        <p className="nis2-muted nis2-dnsc-none" data-sum-none>
          No requirement matches {active.join(', ')} - clear {active.length > 1 ? 'them' : 'it'} to see more.
        </p>
      ) : null}

      {visible('have') && lists ? (
        <details className="nis2-sum-group nis2-sum-have" data-sum-group="have" open={filters.group === 'have' || (narrowed && view.have.length > 0)}>
          <summary>
            <i className="fas fa-check-circle" aria-hidden="true"></i> Have <span className="nis2-sum-n">{view.have.length}</span>
            <span className="nis2-sum-note">documentation and implementation at {target} or more, or not applicable</span>
          </summary>
          {view.have.length === 0 ? <p className="nis2-muted">nothing yet</p> : view.have.map((row) => <HaveRow key={row.requirement.code} row={row} />)}
        </details>
      ) : null}

      {visible('fix') && lists ? (
        <section className="nis2-sum-group" data-sum-group="fix">
          <div className="adm-section adm-section-grey nis2-dnsc-group-head">
            <h4>
              <i className="fas fa-wrench" aria-hidden="true"></i> We fix{' '}
              <span className="nis2-sum-n" data-sum-fix-count>
                {fixView === 'fix' && view.fix.length > 0
                  ? `${plural(fixes.length, 'control')} in ${plural(view.fix.length, 'requirement')}`
                  : view.fix.length}
              </span>
            </h4>
            <span className="adm-section-note">measured below {target} by the platform: each failing control, where, and what to do</span>
            {view.fix.length > 0 ? (
              <div className="btn-group btn-group-xs nis2-sum-fixview" role="group" aria-label="List we fix">
                {(
                  [
                    ['requirement', 'By requirement'],
                    ['fix', 'By fix'],
                  ] as Array<['requirement' | 'fix', string]>
                ).map(([v, label]) => (
                  <button
                    key={v}
                    type="button"
                    className={`btn btn-default${fixView === v ? ' active' : ''}`}
                    aria-pressed={fixView === v}
                    data-sum-fixview={v}
                    onClick={() => setFixView(v)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          {view.fix.length === 0 ? (
            <p className="nis2-muted">{noProposals ? 'not measured: what the platform measures could not be read' : `nothing measured below ${target}`}</p>
          ) : fixView === 'fix' ? (
            fixes.length === 0 ? (
              <p className="nis2-muted" data-sum-fix-none>
                no failing control matches the search
              </p>
            ) : (
              fixes.map((c) => <FixControlRow key={c.key} c={c} />)
            )
          ) : (
            view.fix.map((row) => <FixRow key={row.requirement.code} row={row} target={target} />)
          )}
        </section>
      ) : null}

      {visible('fill') && lists ? (
        <section className="nis2-sum-group" data-sum-group="fill">
          <div className="adm-section adm-section-grey nis2-dnsc-group-head">
            <h4>
              <i className="fas fa-pen" aria-hidden="true"></i> You fill in <span className="nis2-sum-n">{view.fill.length}</span>
            </h4>
            <span className="adm-section-note">
              {noProposals
                ? 'your answer decides these - what the platform measures could not be read'
                : 'your answer decides these - the documentation is always yours, and any figure the platform measures is on the row'}
            </span>
          </div>
          {view.fill.length === 0 ? (
            <p className="nis2-muted">nothing left to answer</p>
          ) : (
            view.fill.map((row) => <FillRow key={row.requirement.code} row={row} canAnswer={canAnswer} onAnswer={onOpenDnsc} />)
          )}
        </section>
      ) : null}
    </div>
  )
}
